/** Splits a CSS font source list without breaking quoted or data URL commas. */
function splitFontSources(source: string): readonly string[] {
  const candidates: string[] = [];
  let start = 0;
  let depth = 0;
  let quote: '"' | "'" | null = null;
  let escaped = false;

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index];
    if (escaped) {
      escaped = false;
      continue;
    }
    if (character === "\\") {
      escaped = true;
      continue;
    }
    if (quote !== null) {
      if (character === quote) {
        quote = null;
      }
      continue;
    }
    if (character === '"' || character === "'") {
      quote = character;
    } else if (character === "(") {
      depth += 1;
    } else if (character === ")") {
      depth = Math.max(0, depth - 1);
    } else if (character === "," && depth === 0) {
      candidates.push(source.slice(start, index).trim());
      start = index + 1;
    }
  }

  candidates.push(source.slice(start).trim());
  return candidates.filter((candidate) => candidate.length > 0);
}

/** Accepts only same-origin, generated, or installed-local font candidates. */
function isLocalFontSource(candidate: string, baseUrl: URL): boolean {
  if (/^local\(/i.test(candidate)) {
    return true;
  }
  const match = /^url\(\s*(["']?)(.*?)\1\s*\)/i.exec(candidate);
  if (match?.[2] === undefined) {
    return false;
  }
  try {
    const url = new URL(match[2], baseUrl);
    return url.origin === baseUrl.origin || url.protocol === "blob:" || url.protocol === "data:";
  } catch {
    return false;
  }
}

/** Removes remote fallbacks before the browser can request or report them. */
export function retainLocalFontSources(source: string, baseUrl: URL): string {
  if (source.trim().length === 0) {
    return source;
  }
  const localSources = splitFontSources(source).filter((candidate) =>
    isLocalFontSource(candidate, baseUrl),
  );
  if (localSources.length === 0) {
    throw new TypeError("Shotboard rejected a font without a local source.");
  }
  return localSources.join(", ");
}

/** Enforces the local-only font boundary for embedded editor dependencies. */
export function installLocalOnlyFontFace(): void {
  const BrowserFontFace = window.FontFace;

  /** Sanitizes string source lists before invoking the browser implementation. */
  class LocalOnlyFontFace extends BrowserFontFace {
    public constructor(
      family: string,
      source: string | BufferSource,
      descriptors?: FontFaceDescriptors,
    ) {
      super(
        family,
        typeof source === "string"
          ? retainLocalFontSources(source, new URL(window.location.href))
          : source,
        descriptors,
      );
    }
  }

  window.FontFace = LocalOnlyFontFace;
}
